/**
 * Authentication Context Provider
 * 
 * Shell SIMAAS Compliance:
 * - Automatic token refresh (Section 5.1.1)
 * - Refresh before expiration (10 minutes before 15-minute expiry)
 * - One-time use refresh tokens (Section 5.4.1)
 */
import React, { createContext, useContext, useEffect, useState, useRef, ReactNode } from 'react';

import {
  clearAccessToken,
  getAccessToken,
  setAccessToken,
} from '../services/accessTokenManager';
import { entraIDAuthService } from '../services/entraIdAuth';
import { projectStateService } from '../services/projectStateService';
import {
  clearRefreshToken,
  getRefreshToken,
  setRefreshToken,
} from '../services/refreshTokenManager';
import { AuthContextType, EntraIDUser, AuthError, RefreshTokenResponse } from '../types/auth';
import { logger } from '../utils/logger';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

/** HTTP status codes indicating the refresh token is permanently invalid. */
const AUTH_FAILURE_STATUSES = [401, 403];
const ACCESS_TOKEN_EXPIRY_FALLBACK_SECONDS = 900;
const REFRESH_BUFFER_SECONDS = 300;
const MINIMUM_REFRESH_INTERVAL_MS = 60000;
const TOKEN_VALIDATION_TIMEOUT_MS = 5000;
const AUTH_USER_STORAGE_KEY = 'auth_user';
const LEGACY_AUTH_TOKEN_STORAGE_KEY = 'auth_token';
const LEGACY_REFRESH_TOKEN_STORAGE_KEY = 'refresh_token';
const LEGACY_TOKEN_EXPIRES_IN_STORAGE_KEY = 'token_expires_in';

/** Characters allowed in auth user fields persisted to browser storage. */
const ALLOWED_AUTH_FIELD_CHARACTERS = [
  'ABCDEFGHIJKLMNOPQRSTUVWXYZ',
  'abcdefghijklmnopqrstuvwxyz',
  '0123456789',
  '@._+- ',
].join('');

/** Maximum length persisted for a single auth user field. */
const MAX_AUTH_FIELD_LENGTH = 256;

/** Fallback used when a user identifier cannot be derived safely. */
const ANONYMOUS_USER_ID = 'anonymous';

/**
 * Custom error for token refresh failures that preserves HTTP status.
 */
class TokenRefreshError extends Error {
  constructor(message: string, public readonly status?: number) {
    super(message);
    this.name = 'TokenRefreshError';
    this.status = status;
  }
}

interface AuthProviderProps {
  children: ReactNode;
}

const clearLegacyTokenStorage = (): void => {
  localStorage.removeItem(LEGACY_AUTH_TOKEN_STORAGE_KEY);
  localStorage.removeItem(LEGACY_REFRESH_TOKEN_STORAGE_KEY);
  localStorage.removeItem(LEGACY_TOKEN_EXPIRES_IN_STORAGE_KEY);
  sessionStorage.removeItem(LEGACY_AUTH_TOKEN_STORAGE_KEY);
  sessionStorage.removeItem(LEGACY_REFRESH_TOKEN_STORAGE_KEY);
  sessionStorage.removeItem(LEGACY_TOKEN_EXPIRES_IN_STORAGE_KEY);
};

const getStoredAuthUser = (): EntraIDUser | null => {
  try {
    const storedUser = localStorage.getItem(AUTH_USER_STORAGE_KEY);
    return storedUser ? (JSON.parse(storedUser) as EntraIDUser) : null;
  } catch {
    return null;
  }
};

/**
 * Rebuild a value as a string composed exclusively of characters taken from
 * the constant `ALLOWED_AUTH_FIELD_CHARACTERS` allow-list, truncated to
 * `MAX_AUTH_FIELD_LENGTH`. Every character of the result originates from a
 * constant, so no externally controlled data reaches browser storage
 * (SonarQube tssecurity:S8475 - sanitize tainted data before writing to
 * browser storage).
 */
const toSafeAuthFieldValue = (value: unknown): string => {
  if (typeof value !== 'string') {
    return '';
  }

  const length = Math.min(value.length, MAX_AUTH_FIELD_LENGTH);
  let safeValue = '';

  for (let index = 0; index < length; index += 1) {
    const position = ALLOWED_AUTH_FIELD_CHARACTERS.indexOf(value.charAt(index));
    if (position !== -1) {
      safeValue += ALLOWED_AUTH_FIELD_CHARACTERS.charAt(position);
    }
  }

  return safeValue;
};

/**
 * Normalise a user payload from an auth response into a plain object
 * containing only the known `EntraIDUser` fields, each rebuilt from the
 * allow-list. Unexpected properties and types are discarded before the value
 * is persisted to localStorage.
 */
const sanitizeAuthUser = (rawUser: unknown): EntraIDUser => {
  const source = (rawUser ?? {}) as Record<string, unknown>;
  const asOptionalSafeString = (value: unknown): string | undefined => {
    const safeValue = toSafeAuthFieldValue(value);
    return safeValue.length > 0 ? safeValue : undefined;
  };

  return {
    username: toSafeAuthFieldValue(source.username),
    email: asOptionalSafeString(source.email),
    name: asOptionalSafeString(source.name),
    sub: asOptionalSafeString(source.sub),
  };
};

/**
 * Derive a storage-safe user identifier from an authentication response.
 */
const resolveSafeUserId = (rawUser: unknown): string => {
  const source = (rawUser ?? {}) as Record<string, unknown>;
  const candidates = [source.id, source.sub, source.email];

  for (const candidate of candidates) {
    const safeValue = toSafeAuthFieldValue(
      typeof candidate === 'number' ? candidate.toString() : candidate,
    );
    if (safeValue.length > 0) {
      return safeValue;
    }
  }

  return ANONYMOUS_USER_ID;
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [user, setUser] = useState<EntraIDUser | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [tokenExpiresIn, setTokenExpiresIn] = useState<number>(
    ACCESS_TOKEN_EXPIRY_FALLBACK_SECONDS,
  );
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const refreshTimerRef = useRef<NodeJS.Timeout | null>(null);
  const refreshRequestRef = useRef<Promise<RefreshTokenResponse> | null>(null);

  useEffect(() => {
    // Check for existing authentication on app load
    initializeAuth();
  }, []);

  // Shell SIMAAS: Automatic token refresh (Section 5.1.1)
  // Refresh 5 minutes before expiration (15 min token - 5 min buffer = 10 min)
  useEffect(() => {
    if (!token || !isAuthenticated) {
      return;
    }

    const setupTokenRefresh = () => {
      // Shell: Access token = 15 minutes (900 seconds)
      // Refresh 5 minutes (300 seconds) before expiration
      const refreshTime = (tokenExpiresIn - REFRESH_BUFFER_SECONDS) * 1000;
      
      // Minimum refresh time of 1 minute for safety
      const safeRefreshTime = Math.max(
        refreshTime,
        MINIMUM_REFRESH_INTERVAL_MS,
      );
      
      refreshTimerRef.current = setTimeout(async () => {
        try {
          await refreshAccessToken();
        } catch (error) {
          // Auth failures (401/403): refresh token is permanently
          // invalid — log out immediately
          // Transient errors (network/5xx): let the token expire
          // naturally — next API call retries via handleUnauthorized
          const isAuthFailure = error instanceof TokenRefreshError &&
            error.status !== undefined &&
            AUTH_FAILURE_STATUSES.includes(error.status);

          if (isAuthFailure) {
            await logout();
          } else {
            logger.warn(
              'Token refresh failed (transient), will retry on next API call',
              error,
            );
          }
        }
      }, safeRefreshTime);
    };

    setupTokenRefresh();

    return () => {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
      }
    };
  }, [token, isAuthenticated, tokenExpiresIn]);

  const initializeAuth = async () => {
    try {
      clearLegacyTokenStorage();

      // Check if we're in an OAuth callback flow - if so, skip token validation
      const urlParams = new URLSearchParams(globalThis.location.search);
      const isOAuthCallback = urlParams.has('code') && urlParams.has('state');
      const isCallbackPage = globalThis.location.pathname === '/auth/callback' || globalThis.location.pathname === '/login';
      
      if (isOAuthCallback && isCallbackPage) {
        // Skipping auth initialization - in OAuth callback flow
        setIsLoading(false);
        return;
      }

      const currentRefreshToken = getRefreshToken();
      if (!currentRefreshToken) {
        setIsLoading(false);
        return;
      }
      
      try {
        await refreshAccessToken();
        const activeToken = getAccessToken();
        if (!activeToken) {
          setIsLoading(false);
          return;
        }

        const cachedUser = getStoredAuthUser();
        setToken(activeToken);
        setUser(cachedUser);
        setIsAuthenticated(true);

        const timeoutPromise = new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Token validation timeout')),
            TOKEN_VALIDATION_TIMEOUT_MS),
        );

        Promise.race([
          entraIDAuthService.getCurrentUser(activeToken),
          timeoutPromise,
        ])
          .then((currentUser) => {
            const validatedUser = sanitizeAuthUser(currentUser);
            setUser(validatedUser);
            localStorage.setItem(
              AUTH_USER_STORAGE_KEY,
              JSON.stringify(validatedUser),
            );
          })
          .catch(() => {
            // Keep active session when validation is transiently unavailable.
          });
      } catch {
        clearAccessToken();
        setToken(null);
        setUser(null);
        setIsAuthenticated(false);
      }
    } catch (authError) {
      logger.error('Auth initialization error:', authError);
      // Only set error if we're not in an OAuth callback flow
      const urlParams = new URLSearchParams(globalThis.location.search);
      const isOAuthCallback = urlParams.has('code') && urlParams.has('state');
      if (!isOAuthCallback) {
        setError('Failed to initialize authentication');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const login = async () => {
    setError(null);
    try {
      await entraIDAuthService.initiateLogin();
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Login failed');
    }
  };

  const refreshAccessToken = async () => {
    /**
     * Refresh access token using refresh token.
     * 
     * Shell SIMAAS Compliance:
     * - Section 5.4.1: One-time use refresh tokens
     * - Each refresh returns NEW access token + NEW refresh token
     * - Old refresh token is automatically invalidated by backend
     */
    const currentRefreshToken = getRefreshToken();
    if (!currentRefreshToken) {
      throw new TokenRefreshError('No refresh token available');
    }

    if (!refreshRequestRef.current) {
      refreshRequestRef.current = (async () => {
        let response: Response;
        try {
          response = await fetch(`${import.meta.env.REACT_APP_API_URL}/auth/refresh`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ refresh_token: currentRefreshToken }),
          });
        } catch {
          throw new TokenRefreshError('Network error during token refresh');
        }

        if (!response.ok) {
          throw new TokenRefreshError(
            `Token refresh failed (${response.status})`,
            response.status,
          );
        }

        return response.json() as Promise<RefreshTokenResponse>;
      })()
        .finally(() => {
          refreshRequestRef.current = null;
        });
    }

    const data = await refreshRequestRef.current;

    setAccessToken(data.access_token);
    setRefreshToken(data.refresh_token);
    // Update state with new access token
    setToken(data.access_token);
    setTokenExpiresIn(data.expires_in || ACCESS_TOKEN_EXPIRY_FALLBACK_SECONDS);
  };

  const logout = async () => {
    try {
      if (token) {
        await entraIDAuthService.logout(token);
      }
    } catch (logoutError) {
      // Logout API call failed, continue with local cleanup
    } finally {
      // Clear refresh timer
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
      }
      
      // Always clear local state
      clearAccessToken();
      clearRefreshToken();
      clearLegacyTokenStorage();
      localStorage.removeItem(AUTH_USER_STORAGE_KEY);
      setToken(null);
      setTokenExpiresIn(ACCESS_TOKEN_EXPIRY_FALLBACK_SECONDS);
      setUser(null);
      setIsAuthenticated(false);
      setError(null);
    }
  };

  const handleAuthSuccess = (authResponse: any) => {
    // Clear error first
    setError(null);
    
    if (!authResponse.refresh_token) {
      handleAuthError({
        message: 'Missing refresh token in authentication response',
      });
      return;
    }

    setAccessToken(authResponse.access_token);
    setRefreshToken(authResponse.refresh_token);
    clearLegacyTokenStorage();
    const sanitizedUser = sanitizeAuthUser(authResponse.user);
    localStorage.setItem(AUTH_USER_STORAGE_KEY, JSON.stringify(sanitizedUser));
    
    // Update state immediately
    setToken(authResponse.access_token);
    setTokenExpiresIn(
      authResponse.expires_in || ACCESS_TOKEN_EXPIRY_FALLBACK_SECONDS,
    );
    setUser(sanitizedUser);
    setIsAuthenticated(true);
    setIsLoading(false);
    
    // Set user ID in projectStateService for localStorage key generation
    projectStateService.setUserId(resolveSafeUserId(authResponse.user));
  };

  const handleAuthError = (error: AuthError) => {
    setError(error.message);
    setIsAuthenticated(false);
    setUser(null);
    setToken(null);
    setTokenExpiresIn(ACCESS_TOKEN_EXPIRY_FALLBACK_SECONDS);
    
    // Clear refresh timer
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
    }
    
    // Clear localStorage on error
    clearAccessToken();
    clearRefreshToken();
    clearLegacyTokenStorage();
    localStorage.removeItem(AUTH_USER_STORAGE_KEY);
  };

  const clearError = () => {
    setError(null);
  };

  /**
   * Handle 401 Unauthorized errors from API calls.
   * Attempts to refresh the token and returns whether refresh succeeded.
   * 
   * @returns true if token was refreshed successfully, false if user needs to re-login
   */
  const handleUnauthorized = async (): Promise<boolean> => {
    try {
      await refreshAccessToken();
      return true; // Token refreshed successfully
    } catch (error) {
      // Refresh failed - user needs to log in again
      await logout();
      return false;
    }
  };

  const value: AuthContextType = {
    isAuthenticated,
    user,
    token,
    login,
    logout,
    isLoading,
    error,
  };

  // Expose auth success/error handlers for callback component
  (value as any).handleAuthSuccess = handleAuthSuccess;
  (value as any).handleAuthError = handleAuthError;
  (value as any).clearError = clearError;
  (value as any).handleUnauthorized = handleUnauthorized;

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
